import React, { useEffect, useState } from "react";
import api from "../../lib/api";
import { toast } from "sonner";
import { Plus, Trash2 } from "lucide-react";

export default function AdminProducts() {
  const [products, setProducts] = useState([]);
  const [editing, setEditing] = useState(null);

  const load = () => api.get("/products").then((r) => setProducts(r.data || []));
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!editing) return;
    try {
      if (editing.product_id && products.find((p) => p.product_id === editing.product_id)) {
        await api.put(`/products/${editing.product_id}`, editing);
        toast.success("Saved");
      } else {
        await api.post("/products", editing);
        toast.success("Created");
      }
      setEditing(null);
      load();
    } catch {
      toast.error("Could not save");
    }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this product?")) return;
    await api.delete(`/products/${id}`);
    toast.success("Deleted");
    load();
  };

  return (
    <div className="p-8 md:p-12" data-testid="admin-products">
      <header className="mb-8 flex items-end justify-between">
        <div>
          <p className="text-[11px] tracking-luxury uppercase text-poche-gold">Atelier</p>
          <h1 className="font-serif text-5xl mt-2">Products</h1>
        </div>
        <button data-testid="admin-add-product" onClick={() => setEditing({ name: "", subtitle: "", collection: "Signature", category: "Sunglasses", price: 0, description: "", material: "", lens_type: "", frame_width: 145, lens_width: 54, bridge_width: 18, temple_length: 145, weight: 25, images: [], colors: ["#0A0A0A"], stock: 50 })} className="btn-gold !py-3 !px-5">
          <Plus size={14} /> New Product
        </button>
      </header>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {products.map((p) => (
          <div key={p.product_id} className="bg-poche-ink border border-white/10 p-4" data-testid={`admin-product-${p.product_id}`}>
            <div className="bg-poche-ivory aspect-[4/3] overflow-hidden">
              <img src={p.images?.[0]} alt={p.name} className="w-full h-full object-cover" />
            </div>
            <div className="mt-3 flex items-start justify-between">
              <div>
                <div className="text-[10px] tracking-luxury uppercase text-white/55">{p.collection}</div>
                <div className="font-serif text-lg">{p.subtitle}</div>
                <div className="text-poche-gold text-sm">${p.price.toLocaleString()}</div>
                <div className="text-[10px] text-white/45 mt-1">Stock: {p.stock ?? "—"} · {p.category}</div>
              </div>
              <div className="flex flex-col gap-2">
                <button onClick={() => setEditing(p)} className="text-[10px] tracking-luxury uppercase text-poche-gold hover:underline">Edit</button>
                <button onClick={() => del(p.product_id)} className="text-[10px] tracking-luxury uppercase text-red-400 hover:underline">Delete</button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {editing && (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-6" onClick={() => setEditing(null)}>
          <div className="bg-poche-ink border border-poche-gold/40 max-w-2xl w-full p-8 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-serif text-2xl mb-6">{editing.product_id ? "Edit Product" : "New Product"}</h3>
            <div className="grid grid-cols-2 gap-4">
              <Field l="Name" v={editing.name} on={(v) => setEditing({ ...editing, name: v })} />
              <Field l="Subtitle" v={editing.subtitle} on={(v) => setEditing({ ...editing, subtitle: v })} />
              <Field l="Collection" v={editing.collection} on={(v) => setEditing({ ...editing, collection: v })} />
              <Field l="Category" v={editing.category} on={(v) => setEditing({ ...editing, category: v })} />
              <Field l="Price" type="number" v={editing.price} on={(v) => setEditing({ ...editing, price: Number(v) })} />
              <Field l="Stock" type="number" v={editing.stock} on={(v) => setEditing({ ...editing, stock: Number(v) })} />
              <Field l="Material" v={editing.material} on={(v) => setEditing({ ...editing, material: v })} />
              <Field l="Lens Type" v={editing.lens_type} on={(v) => setEditing({ ...editing, lens_type: v })} />
              <Field l="Frame Width (mm)" type="number" v={editing.frame_width} on={(v) => setEditing({ ...editing, frame_width: Number(v) })} />
              <Field l="Bridge Width (mm)" type="number" v={editing.bridge_width} on={(v) => setEditing({ ...editing, bridge_width: Number(v) })} />
              <Field l="Image URL 1" v={editing.images?.[0] || ""} on={(v) => setEditing({ ...editing, images: [v, editing.images?.[1] || ""] })} cls="col-span-2" />
              <Field l="Description" v={editing.description} on={(v) => setEditing({ ...editing, description: v })} cls="col-span-2" textarea />
            </div>
            <div className="flex gap-3 mt-6">
              <button data-testid="admin-save-product" onClick={save} className="btn-gold flex-1 justify-center">Save</button>
              <button onClick={() => setEditing(null)} className="btn-outline-gold flex-1 justify-center">Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Field({ l, v, on, type = "text", textarea, cls = "" }) {
  return (
    <div className={cls}>
      <label className="text-[10px] tracking-luxury uppercase text-white/55">{l}</label>
      {textarea ? (
        <textarea value={v} onChange={(e) => on(e.target.value)} rows={3} className="w-full bg-transparent border-b border-white/25 focus:border-poche-gold py-2 text-white focus:outline-none" />
      ) : (
        <input type={type} value={v} onChange={(e) => on(e.target.value)} className="w-full bg-transparent border-b border-white/25 focus:border-poche-gold py-2 text-white focus:outline-none" />
      )}
    </div>
  );
}
